import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useLearningManagementServiceV1 } from '../api'
import {
  EventTemplateWithoutId,
  Implementation,
  LearningEvent,
} from '../auto-generated-clients/learning-management-client-v1.generated'
import { EventTemplateWithImplementation } from '../models/eventTemplateWithImplementation'
import { useCompanyData } from './useCompanyData'
import { useEffect, useMemo } from 'react'

const IMPLEMENTATIONS_QUERY_KEY = 'implementations'
const EVENT_TEMPLATES_QUERY_KEY = 'eventTemplates'
const EMPTY_IMPLEMENTATION = new Implementation({
  companyId: '',
  implementationId: '',
  implementationName: '', 
  rootLMSUrl: '',  
  defaultTemplate: '',
})

interface UseEventTemplatesArgs {
  selectedLearningEvent: LearningEvent | undefined
  selectedEventTemplate?: EventTemplateWithImplementation
  setSelectedEventTemplate: (
    eventTemplate: EventTemplateWithImplementation | undefined
  ) => void
  isEditingEventTemplate: boolean
}

const PAGE_SIZE = 1000

export const useEventTemplates = ({
  selectedLearningEvent,
  selectedEventTemplate,
  setSelectedEventTemplate,
  isEditingEventTemplate,
}: UseEventTemplatesArgs) => {
  const lmsV1 = useLearningManagementServiceV1()
  const queryClient = useQueryClient()
  const { data: companies } = useCompanyData()

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: [EVENT_TEMPLATES_QUERY_KEY],
    })
  }

  const fetchImplementations = () => lmsV1.getAllImplementations(0, PAGE_SIZE)

  const fetchEventTemplates = ({ pageParam = 0 }) =>
    lmsV1.getAllEventTemplates(undefined, pageParam, PAGE_SIZE)

  const updateEventTemplate = async (updatedTemplate: string) => {
    if (selectedEventTemplate?.eventTemplateId) {
      const updatedEventTemplate = new EventTemplateWithoutId({
        implementationId: selectedEventTemplate.implementationId,
        learningEventId: selectedLearningEvent?.learningEventId ?? '',
        template: updatedTemplate,
      })

      await lmsV1.updateEventTemplate(
        selectedEventTemplate.eventTemplateId,
        updatedEventTemplate
      )
    }
  }

  const { data: implementations, isLoading: isImplementationsQueryLoading } =
    useQuery({
      queryKey: [IMPLEMENTATIONS_QUERY_KEY],
      queryFn: fetchImplementations,
    })

  const {
    data: eventTemplatesData,
    isLoading: isEventTemplatesQueryLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [EVENT_TEMPLATES_QUERY_KEY],
    queryFn: fetchEventTemplates,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === PAGE_SIZE ? allPages.length : undefined,
    initialPageParam: 0,
  })

  useEffect(() => {
    if (!isEventTemplatesQueryLoading && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [
    isEventTemplatesQueryLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  ])

  const eventTemplates = useMemo(
    () => eventTemplatesData?.pages.flat() ?? [],
    [eventTemplatesData]
  )

  const implementationsWithCompanyName = useMemo(
    () =>
      implementations?.map((i) => {
        const company = companies?.find((c) => c.companyId === i.companyId)

        return {
          ...i,
          companyName: company?.friendlyName,
        }
      }),
    [implementations, companies]
  )

  const eventTemplatesToBeDisplayed = useMemo(() => {
    return (
      eventTemplates
        ?.filter(
          (e) => e.learningEventId === selectedLearningEvent?.learningEventId
        )
        .map((event) => {
          const implementation =
            implementationsWithCompanyName?.find(
              (i) => event.implementationId === i.implementationId
            ) ?? EMPTY_IMPLEMENTATION

          return {
            ...event,
            ...implementation,
          }
        }) ?? []
    )
  }, [eventTemplates, selectedLearningEvent, implementationsWithCompanyName])

  const addEventTemplate = (implementationId: string) => {
    if (isEditingEventTemplate) {
      return Promise.reject(
        new Error(
          'Please save or cancel the current event template before adding a new event template.'
        )
      )
    }
    const implementation = implementations?.find(
      (i) => i.implementationId === implementationId
    )

    const newEventTemplate = new EventTemplateWithoutId({
      implementationId,
      learningEventId: selectedLearningEvent?.learningEventId ?? '',
      template: implementation?.defaultTemplate ?? '',
    })
    return lmsV1.createEventTemplate(newEventTemplate)
  }

  const { mutate: addEventTemplateMutation } = useMutation({
    mutationFn: addEventTemplate,
    onSuccess: async (addedEvenTemplate) => {
      invalidateQueries()
      const addedEventTemplateToBeDisplayed = {
        ...addedEvenTemplate,
        ...(implementationsWithCompanyName?.find(
          (i) => i.implementationId === addedEvenTemplate.implementationId
        ) ?? EMPTY_IMPLEMENTATION),
      }
      setSelectedEventTemplate(addedEventTemplateToBeDisplayed)
    },
    onError: (error) => {
      alert(error.message)
    },
  })

  const deleteEventTemplate = async () => {
    if (isEditingEventTemplate) {
      return Promise.reject(
        new Error(
          'Please save or cancel the current event template before deleting an event template.'
        )
      )
    }
    const deletedEventIndex = eventTemplatesToBeDisplayed?.findIndex(
      (e) => e.eventTemplateId === selectedEventTemplate?.eventTemplateId
    )
    if (selectedEventTemplate?.eventTemplateId) {
      await lmsV1.deleteEventTemplateById(
        selectedEventTemplate?.eventTemplateId
      )
    }

    return deletedEventIndex ?? 0
  }

  const { mutate: deleteEventTemplateMutation } = useMutation({
    mutationFn: deleteEventTemplate,
    onSuccess: (deletedIndex) => {
      const newSelectedIndex = deletedIndex === 0 ? 1 : deletedIndex - 1
      invalidateQueries()
      setSelectedEventTemplate(eventTemplatesToBeDisplayed?.[newSelectedIndex])
    },
    onError: (error) => {
      alert(error.message)
    },
  })

  const { mutate: updateEventTemplateMutation } = useMutation({
    mutationFn: updateEventTemplate,
    onSuccess: invalidateQueries,
  })

  return {
    implementations,
    isImplementationsQueryLoading,
    eventTemplates,
    isEventTemplatesQueryLoading,
    implementationsWithCompanyName,
    eventTemplatesToBeDisplayed,
    addEventTemplateMutation,
    deleteEventTemplateMutation,
    updateEventTemplateMutation,
  }
}
